/**
 * 변수 설정 목록 조회 팝업 (ID / ID명 / 타입 / Default / Format).
 *
 * 변수 속성(SnVarProps)에 등록한 값을 "보여주기만" 한다. 추가/삭제/편집이 없다.
 * 맨 앞 "No" 열은 SnGrid가 자동으로 붙이므로(SnGrid.tsx:329-347) 여기서 정의하지 않는다.
 *
 * 컬럼 메타(sys_formgridcolumn)가 DB에 없으므로 columnConfig를 코드로 만들어 넘긴다.
 */
import React, { forwardRef, useEffect, useRef, useState } from "react";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { type ColumnConfig } from "@shared/components/GridV2/SnGridTypes";
import { getCodeData } from "@shared/utils/CommonUtils";

/** 변수 설정 한 행 */
export interface VarSet {
  /** 변수 ID — 본문에 @%ID@% 로 꽂힌다 */
  id: string;
  /** ID명 — 이 변수가 무엇인지 적어두는 자리 (예: 문서번호) */
  name: string;
  /** 타입 — 공통코드에서 고른다 */
  type: string;
  /** 기본값 — 예약어(오늘/부서명 등)도 쓸 수 있다 */
  default: string;
  /** 표현 포맷 — 예: yyyy-mm-dd */
  format: string;
}

/**
 * 타입 선택 목록의 공통코드 분류코드(cd_clas).
 * 목록은 공통코드관리(SN_MAS_SET_REG_001)에서 등록/수정한다.
 * 변수 속성과 다른 목록을 쓰려면 이 값만 바꾸면 된다.
 */
export const VARSET_TYPE_CD_CLAS = "SYS205";

/**
 * ColumnConfig는 DB에서 읽은 sys_formgridcolumn 행을 전제로 cd_firm/cd_menu/cd_user를
 * 필수로 요구하는데, 이 목록은 코드로 만든 것이라 해당 없다.
 */
const COLUMN_BASE = { cd_firm: "", cd_menu: "", cd_user: "", cd_grid: "grid1" };

/**
 * 타입 컬럼의 선택 목록은 공통코드에서 읽어 js_combo에 넣는다.
 * GridV2의 SnComboBox는 js_combo만 읽고 cd_clas 폴백이 없다(SnColumnsCreate.ts:376-380).
 * 평소엔 백엔드 getInitForm이 채워주지만 이 columnConfig는 코드로 만든 것이라 직접 넣는다.
 */
const buildColumns = (typeOptions: any[]): ColumnConfig[] => [
  {
    ...COLUMN_BASE,
    cd_column: "id",
    nm_column: "ID",
    tp_column: "SnTextBox",
    qt_width: 150,
    fg_visible: "Y",
    yn_required: "Y",
    no_seq: 1,
  },
  {
    ...COLUMN_BASE,
    cd_column: "name",
    nm_column: "ID명",
    tp_column: "SnTextBox",
    qt_width: 150,
    fg_visible: "Y",
    no_seq: 2,
  },
  {
    ...COLUMN_BASE,
    cd_column: "type",
    nm_column: "타입",
    tp_column: "SnComboBox",
    cd_clas: VARSET_TYPE_CD_CLAS,
    js_combo: JSON.stringify(typeOptions),
    qt_width: 120,
    fg_visible: "Y",
    no_seq: 3,
  },
  {
    ...COLUMN_BASE,
    cd_column: "default",
    nm_column: "Default",
    tp_column: "SnTextBox",
    qt_width: 150,
    fg_visible: "Y",
    no_seq: 4,
  },
  {
    ...COLUMN_BASE,
    cd_column: "format",
    nm_column: "Format",
    tp_column: "SnTextBox",
    qt_width: 150,
    fg_visible: "Y",
    no_seq: 5,
  },
];

/**
 * 컨트롤 값(JSON 문자열)에 담긴 변수 설정 목록을 배열로 되돌린다.
 *
 * 같은 값이 경로에 따라 네 가지 모양으로 내려온다. 넷 다 받아야 목록이 뜬다.
 *   1) 배열           [{...}]
 *   2) JSON 문자열     '[{...}]'
 *   3) 객체 래퍼       {type:'jsonb', value:'[…]'}
 *   4) 이중 인코딩     '"[{...}]"'   (jsonb에 JSON 스칼라로 저장된 경우)
 */
export const parseVarSet = (raw: any): VarSet[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;

  if (typeof raw === "object") {
    const inner = (raw as any).value;
    return typeof inner === "string" ? parseVarSet(inner) : [];
  }
  if (typeof raw !== "string") return [];

  let value: any = raw;
  for (let i = 0; i < 3; i++) {
    try {
      value = JSON.parse(typeof value === "string" ? value.trim() : value);
    } catch {
      return [];
    }
    if (Array.isArray(value)) return value;
    if (typeof value !== "string") return [];
  }
  return [];
};

interface Props {
  /** 보여줄 목록 */
  defaultValue?: VarSet[];
}

const VarSetListPopup = forwardRef(({ defaultValue }: Props, _ref) => {
  const gridRef = useRef<any>(null);

  /**
   * 타입 셀의 선택 목록(agRichSelectCellEditor)이 그려질 자리.
   *
   * 지정하지 않으면 SnGrid가 #ag-grid-popup-root를 찾다 실패하고 <body>로 떨어진다
   * (SnGrid.tsx:2758-2763 — 그 엘리먼트는 앱 어디에서도 만들지 않는다).
   * <body>는 모달 바깥이라 MUI Dialog의 focus trap이 포커스를 도로 뺏어가고
   * z-index도 모달 아래라, 셀을 눌러도 목록이 뜨자마자 닫혀 클릭이 안 먹는 것처럼 보인다.
   */
  const popupRootRef = useRef<HTMLDivElement>(null);
  const [popupParentEl, setPopupParentEl] = useState<HTMLElement | null>(null);

  // 공통코드를 받아오기 전에는 빈 목록으로 컬럼을 만들어 둔다.
  // 조회가 끝나면 columnConfig가 새 배열이 되어 SnGrid가 컬럼을 다시 만든다(SnGrid.tsx:395).
  const [columns, setColumns] = useState<ColumnConfig[]>(() => buildColumns([]));

  useEffect(() => {
    setPopupParentEl(popupRootRef.current);
  }, []);

  useEffect(() => {
    let alive = true;
    const loadTypeOptions = async () => {
      const data = await getCodeData(VARSET_TYPE_CD_CLAS, "");
      if (!alive) return;
      setColumns(buildColumns(Array.isArray(data) ? data : []));
    };
    loadTypeOptions();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div
      ref={popupRootRef}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <div style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
        {/* 조회 전용 — 편집·체크박스 없이 목록만 보여준다 */}
        <SnGrid
          ref={gridRef}
          columnConfig={columns}
          rowData={defaultValue ?? []}
          etcProps={{
            rowSelection: "single",
            isEditable: false,
            ischeckboxSelection: false,
            popupParent: popupParentEl,
          }}
        />
      </div>
    </div>
  );
});

export default VarSetListPopup;
